/**
 * Haptic Feedback Service
 *
 * Provides haptic feedback (vibration) for touch interactions.
 * Gracefully degrades on unsupported browsers/devices.
 */

/**
 * Check if haptic feedback is supported
 */
export function isHapticSupported(): boolean {
  return typeof navigator !== 'undefined' && 'vibrate' in navigator;
}

/**
 * Trigger a short haptic pulse
 * Used for: hold detection start, button taps
 *
 * @param duration - Vibration duration in ms (default: 50)
 */
export function hapticPulse(duration: number = 50): void {
  if (isHapticSupported()) {
    try {
      navigator.vibrate(duration);
    } catch (e) {
      // Silently fail - haptic is non-critical
      console.debug('[Haptic] Vibration failed:', e);
    }
  }
}

/**
 * Trigger a longer haptic feedback
 * Used for: removal complete, important confirmations
 *
 * @param duration - Vibration duration in ms (default: 150)
 */
export function hapticConfirm(duration: number = 150): void {
  if (isHapticSupported()) {
    try {
      navigator.vibrate(duration);
    } catch (e) {
      console.debug('[Haptic] Vibration failed:', e);
    }
  }
}

/**
 * Trigger a pattern of haptic pulses
 * Used for: warnings, errors
 *
 * @param pattern - Array of [vibrate, pause, vibrate, pause, ...] in ms
 */
export function hapticPattern(pattern: number[]): void {
  if (isHapticSupported()) {
    try {
      navigator.vibrate(pattern);
    } catch (e) {
      console.debug('[Haptic] Vibration failed:', e);
    }
  }
}

/**
 * Cancel any ongoing vibration
 */
export function hapticCancel(): void {
  if (isHapticSupported()) {
    try {
      navigator.vibrate(0);
    } catch (e) {
      console.debug('[Haptic] Vibration cancel failed:', e);
    }
  }
}

/**
 * Pre-defined haptic patterns for removal gesture
 */
export const REMOVAL_HAPTICS = {
  /** Short pulse when hold is detected (500ms threshold reached) */
  holdDetected: () => hapticPulse(50),

  /** Longer pulse when removal completes */
  removalComplete: () => hapticConfirm(150),

  /** Quick double-pulse for cancellation */
  cancelled: () => hapticPattern([30, 50, 30]),
} as const;
